import * as Sentry from "@sentry/react";
import i18next from "i18next";
import { languageMap } from "@/i18n";
import { supabase } from "@/integrations/supabase/client";
import { parse, format } from "date-fns";
import { FormValues } from "@/components/booking-form/types";
import { BookingLanguage, TimeSlot } from "@/types/booking";
import { toast } from "@/components/ui/use-toast";
import { Product } from "@/components/product-step/types";
import { safeLocalStorage } from "@/utils/safeStorage";
import * as i18nIsoCountries from "i18n-iso-countries";
import { useProducts } from "@/components/product-step/useProducts.ts";
import { getUrlParam, getTestModeParams } from "@/utils/urlUtils";
import { resolveProductKey } from "@/locales";
import { countrySpecificConsent } from "@/utils/countrySpecificConsent";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import { useMembership } from "@/contexts/MembershipContext";
import { MEMBERSHIP_API_ORIGIN } from "@/services/membershipService";
import { useBloodCheckout } from "@/hooks/useBloodCheckout";
import { membershipSignupSource } from "@/utils/membershipSignup";
import {
  buildPricingSummary,
  billedComposition,
  type PricingSummary,
} from "@/utils/bookingPricingSummary";
import { posthogDistinctIdField } from "@/utils/posthogDistinctId";

// Mobile in-app browsers (Instagram, Facebook, etc.) and flaky networks can
// drop fetches to the edge function. Retry any failed invoke a few times
// with exponential backoff before surfacing the failure — the server
// de-duplicates on bookingId so repeated attempts are safe.
//
// That safety also covers a *timed-out* attempt (AHD-8846), where the request
// may well have reached a slow-but-alive worker: every attempt carries the same
// client-generated `bookingId`, and the edge function upserts on
// `idempotency_key = id` with `ignoreDuplicates`, gating the relay / Firebase
// activation / entitlement writes on having actually created the row. So a
// retry that races a first attempt no-ops and returns the existing booking —
// no duplicate booking, no duplicate emails.
const MAX_SUBMIT_ATTEMPTS = 3;
const SUBMIT_RETRY_BASE_MS = 1000;
// Generous — a submit normally lands in <1s. Past this the worker is assumed
// hung, and functions-js aborts the request so the retry loop below engages.
const SUBMIT_TIMEOUT_MS = 30_000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const invokeSubmitBookingWithRetry = async (
  body: Record<string, unknown>,
): Promise<{
  data: unknown;
  error: { name?: string; message?: string } | null;
}> => {
  let lastError: { name?: string; message?: string } | null = null;
  for (let attempt = 1; attempt <= MAX_SUBMIT_ATTEMPTS; attempt++) {
    // `timeout` bounds the request inside functions-js: it aborts the fetch and
    // returns the abort as a normal `error`, so a stalled worker takes the same
    // retry/backoff path as any other failure instead of hanging forever.
    const { data, error } = await supabase.functions.invoke(
      "submit-booking-with-deals-and-firebase-4",
      { body, timeout: SUBMIT_TIMEOUT_MS },
    );
    if (!error) return { data, error: null };
    lastError = error;
    if (attempt === MAX_SUBMIT_ATTEMPTS) {
      return { data: null, error };
    }
    const delay = SUBMIT_RETRY_BASE_MS * 2 ** (attempt - 1);
    console.warn(
      `[BookingSubmission] Edge function invoke failed (attempt ${attempt}/${MAX_SUBMIT_ATTEMPTS}), retrying in ${delay}ms:`,
      error,
    );
    await sleep(delay);
  }
  return { data: null, error: lastError };
};

// Safety net: write the raw booking payload directly to a write-only table
// from the client. If every edge function attempt fails (or the browser dies
// mid-submission), we still have the submitted data captured server-side
// for manual recovery. Fire-and-forget — a logging failure must not disrupt
// the user-facing booking flow.
const recordBookingAttempt = (
  bookingId: string,
  booking: Record<string, unknown>,
) => {
  void supabase
    .from("booking_submission_attempts")
    .insert({
      booking_id: bookingId,
      booking_payload: booking as never,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    })
    .then(({ error }) => {
      if (error) {
        console.warn(
          "[BookingSubmission] Failed to record booking attempt:",
          error,
        );
      }
    });
};

interface UseBookingSubmissionProps {
  bookingId: string;
  selectedTimeSlots: TimeSlot[];
  selectedProducts: string[];
  selectedAddOns?: string[]; // Add selectedAddOns prop
  selectedClinic: string;
  language: string;
  /** Flow marker written to the bookings table. "membership-first" for the new
   *  app; absent (undefined) for the current booking flow. */
  flow?: string;
  discount?: {
    code: string;
    type: "percentage" | "fixed_amount";
    value: number;
  } | null;
  email: string;
  onSubmitSuccess: (data: unknown) => void;
  packageComponentMap?: Record<string, string[]>;
  products?: Product[]; // Add products for GA4 tracking
  totalPrice?: number; // Add totalPrice prop
  /** The money block, when the caller already billed this booking: the
   *  membership-first visit is booked after its checkout, as a member with
   *  everything prepaid, so only the checkout knows what was charged. */
  pricingSummary?: PricingSummary;
  /** Products in the booking that cost nothing: the free Lp(a) a new Swiss
   *  membership gets on its first visit (AHD-14738). They are listed at 0,
   *  so the deal's lines add up to what was charged. */
  giftedProductIds?: string[];
}

export const useBookingSubmission = ({
  bookingId,
  selectedTimeSlots,
  selectedProducts,
  selectedAddOns = [], // Add selectedAddOns with default empty array
  selectedClinic,
  language,
  flow,
  discount,
  email,
  onSubmitSuccess,
  packageComponentMap = {},
  products = [], // Add products with default empty array
  totalPrice,
  pricingSummary: billedPricingSummary,
  giftedProductIds = [],
}: UseBookingSubmissionProps) => {
  const setSubmitting = useBookingFlowStore((state) => state.setSubmitting);
  const setSubmitted = useBookingFlowStore((state) => state.setSubmitted);
  // The active market (country) the booking was made in — relayed to the edge
  // function so the backend knows which market the booking belongs to.
  const market = useBookingFlowStore((state) => state.market);
  const lng = languageMap[language];
  const { isMember, getEffectivePrice, membership } = useMembership();

  const { data } = useProducts(language as BookingLanguage, market);

  // The billed-pricing context for the money block the payload carries
  // (pricing_summary): which path this booking is (membershipSignupSource,
  // the same signal booking_complete uses), the cadence price, and the
  // bundle's per-line amounts when the cart is a scan bundle.
  const membershipCheckoutInitiated = useBookingFlowStore(
    (s) => s.membershipCheckoutInitiated,
  );
  const { expectedMembershipCents, bundle } = useBloodCheckout();

  // Pure, side-effect-free assembly of the submit-4 booking payload. Closes over
  // the hook scope (market, language, bookingId, selectedClinic, time slots,
  // products, discount, membership, pricing, totalPrice, email). Used by
  // handleFormSubmit AND published (via BookingForm) so create-blood-checkout
  // can stash the SAME payload for the durable-booking webhook replay.
  // IMPORTANT: must NOT touch localStorage or any other side effect — the
  // membership_token consume stays in handleFormSubmit so it runs exactly once.
  const buildBookingPayload = (formData: FormValues) => {
    const isTestMode = getUrlParam("e2e") === "true";
    const isInternal = getUrlParam("internal") === "true";
    const { testUserId, reportBackendDomain, testId } = getTestModeParams();
    const membershipDomain = getUrlParam("membershipDomain");

    // Use separate firstName and lastName fields
    const firstName = formData.firstName || "";
    const lastName = formData.lastName || "";

    // In the German market the three private-treatment consents are the legal
    // basis and encompass Terms & Privacy + medical-data processing. When all
    // three are approved, record tos + medical consent on the SERVER payload
    // too (the downstream activation blocks a booking without them). This is a
    // payload-only derivation — it does NOT touch the form checkboxes.
    const deConsentsApproved =
      market === "de" &&
      !!formData.deWunschleistungConsent &&
      !!formData.deTreatmentConsent &&
      !!formData.deConfidentialityConsent;

    // Austria's three consents are national legal text and, like Germany's,
    // they cover the ground the generic tos + medical-processing boxes cover:
    // the lab and partner-doctor contracts, the confidentiality release, and
    // the express request to start early. Recorded on the payload the same way
    // so downstream activation is not blocked. Payload-only, never written back
    // onto the form checkboxes.
    const atConsentsApproved =
      market === "at" &&
      !!formData.atLabAndDoctorConsent &&
      !!formData.atConfidentialityConsent &&
      !!formData.atWithdrawalConsent;

    const consentRecord = countrySpecificConsent({
      market,
      formData,
      language,
      isInternal,
    });

    const birthdayDate = parse(formData.birthday, "dd.MM.yyyy", new Date());

    // Process time slots to identify if any are fast booking slots
    const jsonTimeSlots = selectedTimeSlots.map((slot) => {
      const year = slot.date.getFullYear();
      const month = slot.date.getMonth();
      const day = slot.date.getDate();

      const utcDate = new Date(Date.UTC(year, month, day));

      return {
        date: utcDate.toISOString(),
        time: slot.time,
        isFastBooking: slot.isFastBooking || false,
      };
    });

    // Determine if any slots are fast booking
    const hasFastBookingSlots = jsonTimeSlots.some(
      (slot) => slot.isFastBooking,
    );
    void hasFastBookingSlots;

    // Determine booking type based on slot source:
    // - "direct" if ALL slots are from external system (isFromExternalSlot: true)
    // - "request" if ANY slot is from opening_hours/dialog (isFromExternalSlot: false or undefined)
    const allSlotsFromExternalSystem =
      selectedTimeSlots.length > 0 &&
      selectedTimeSlots.every((slot) => slot.isFromExternalSlot === true);
    const effectiveBookingType: "direct" | "request" =
      allSlotsFromExternalSystem ? "direct" : "request";

    // No placeholder address: this email becomes the Stripe customer and the
    // booking's contact. An empty one fails validation downstream instead.
    const emailToUse = formData.email || email || "";

    // CRITICAL FIX: Ensure total_price is properly passed and logged
    // Use the totalPrice passed via props, falling back to formData if needed (though it shouldn't be there)
    const effectiveTotalPrice =
      totalPrice !== undefined ? totalPrice : formData.total_price || 0;

    // Combine selectedProducts and selectedAddOns for submission
    const allProductIds = [...selectedProducts, ...selectedAddOns];

    // Get selected products and add resolved display names for the webhook
    // This ensures the webhook receives human-readable names, not translation keys
    const allSelectedProductDetails =
      data?.products
        ?.filter((p) => allProductIds.includes(p.id))
        .map((p) => {
          const effectiveAmount = giftedProductIds.includes(p.id)
            ? 0
            : isMember
              ? getEffectivePrice(p)
              : (p.default_price?.unit_amount ?? 0);
          const pricedProduct =
            (isMember || giftedProductIds.includes(p.id)) &&
            p.default_price &&
            effectiveAmount !== p.default_price.unit_amount
              ? {
                  ...p,
                  default_price: {
                    ...p.default_price,
                    unit_amount: effectiveAmount,
                  },
                }
              : p;
          return {
            ...pricedProduct,
            // Add displayName with resolved translation (uses English for webhook consistency)
            displayName: resolveProductKey("en-US", p.name),
          };
        }) || [];

    // Transform phone number to E.164 format (replace leading 00 with +)
    const phoneE164 = formData.phone?.replace(/^00/, "+") || formData.phone;

    // The money block, stated explicitly — the same five numbers the order
    // summary displayed — so the relay/Attio webhook never reverse-engineers
    // them from total_price (whose meaning differs by path: net for
    // pay-later, pre-discount billed base for a signup). See
    // bookingPricingSummary.ts for the incoherent payload this replaces.
    const signup = membershipSignupSource({
      membershipCheckoutInitiated,
      isMember,
    });
    // The baseline (once-a-year) tier price, from the same Stripe-served
    // catalog the cadence came from — never hardcoded, so a repricing moves
    // the deferred-follow-up split with it.
    const baselineCents =
      data?.membershipTiers?.find((t) => t.frequency_per_year === 1)
        ?.annual_amount_chf ?? undefined;
    const composition = billedComposition({
      cart: allSelectedProductDetails,
      signup,
      isMember,
      effectivePriceOf: getEffectivePrice,
      cadenceCents: expectedMembershipCents,
      baselineCents,
      bundleLines: bundle ? bundle.lines : null,
    });
    const pricingSummary =
      billedPricingSummary ??
      buildPricingSummary({
        listCents: composition.listCents,
        billedLines: composition.billedLines,
        discount: discount ?? null,
        futureFollowupCents: composition.futureFollowupCents,
        memberValueCents: composition.memberValueCents,
      });

    const bookingData = {
      email: emailToUse,
      first_name: firstName,
      last_name: lastName,
      birthday: format(birthdayDate, "yyyy-MM-dd"),
      notes: formData.isAnxiousPatient
        ? `${formData.notes || ""}\n[Anxious patient / Angstpatient]`.trim()
        : formData.notes,
      time_slots: jsonTimeSlots,
      booking_type: effectiveBookingType,
      gender_at_birth: formData.genderAtBirth,
      gdpr_consent: formData.gdprConsent,
      product_ids: allProductIds, // Use combined products and addons
      phone: phoneE164,
      discount_code: discount,
      clinic_id: selectedClinic,
      completed: true,
      market,
      // Browser's PostHog distinct_id for the Hub join (EAGNT-1241). The
      // helper spreads nothing when PostHog is blocked/not loaded, so the
      // payload omits the key cleanly and the submit never breaks on it.
      ...posthogDistinctIdField(),
      preferred_language: formData.preferredLanguage || language,
      total_price: effectiveTotalPrice,
      street: formData.street,
      street_number: formData.streetNumber,
      zip_code: formData.zipCode,
      city: formData.city,
      country_code: formData.country,
      country: i18nIsoCountries.getName(formData.country, language.slice(0, 2)),
      // Needed in the edge function
      product_details: allSelectedProductDetails,
      package_component_map: packageComponentMap,
      product_prices: {},
      // Payload-only (stripped before the bookings insert): the explicit
      // money block for the relay/Attio webhook.
      pricing_summary: pricingSummary,
      // Membership entitlements to mark as booked after successful insert
      ...(isMember && membership
        ? {
            membership_entitlement_updates: (() => {
              const seen = new Set<string>();
              // Consume baseline before followup for same product
              const sorted = [...membership.membership_entitlements].sort(
                (a, b) =>
                  a.entitlement_type.includes("baseline")
                    ? -1
                    : b.entitlement_type.includes("baseline")
                      ? 1
                      : 0,
              );
              return sorted
                .filter((e) => {
                  if (e.status !== "available") return false;
                  if (seen.has(e.stripe_product_id)) return false;
                  const matched = allSelectedProductDetails.some(
                    (p) =>
                      p.stripe_product_id === e.stripe_product_id &&
                      getEffectivePrice(p) === 0,
                  );
                  if (matched) seen.add(e.stripe_product_id);
                  return matched;
                })
                .map((e) => ({
                  entitlement_id: e.id,
                  stripe_product_id: e.stripe_product_id,
                }));
            })(),
          }
        : {}),
      // If internal mode, set consent to null. Otherwise use form values.
      // In membership mode the consent was already captured at membership
      // signup — prefer that date over today's so the audit trail points
      // at the original acceptance event, not the re-booking moment.
      tos_and_pp_consent_date: isInternal
        ? null
        : membership?.tosAcceptedAt
          ? format(new Date(membership.tosAcceptedAt), "yyyy-MM-dd")
          : formData.tosAndPpConsent || deConsentsApproved
            ? format(new Date(), "yyyy-MM-dd")
            : null,
      medical_processing_consent_date: isInternal
        ? null
        : membership?.medicalConsentAcceptedAt
          ? format(new Date(membership.medicalConsentAcceptedAt), "yyyy-MM-dd")
          : formData.medicalProcessing ||
              deConsentsApproved ||
              atConsentsApproved
            ? format(new Date(), "yyyy-MM-dd")
            : null,
      anonymous_data_usage_consent_date: isInternal
        ? null
        : formData.anonymousDataUsage
          ? format(new Date(), "yyyy-MM-dd")
          : null,
      // Country-specific consent evidence, one builder per country in
      // countrySpecificConsent.ts. Absent for markets and situations that
      // record nothing, so the key is not sent at all rather than sent empty.
      ...(consentRecord ? { country_specific_consent: consentRecord } : {}),
      test_mode: isTestMode,
      internal: isInternal,
      ...(reportBackendDomain
        ? { report_backend_domain: reportBackendDomain }
        : {}),
      // The membership environment the session read from; the edge function
      // writes the entitlement back there. Without the link's own, the API
      // this build reads from: a preview reads staging's, and the function's
      // default (production's) left a sandbox plan's entitlement unbooked
      // (Laurent, 19.10).
      membership_domain: membershipDomain || MEMBERSHIP_API_ORIGIN,
      ...(testUserId ? { test_user_id: testUserId } : {}),
      ...(testId ? { test_id: testId } : {}),
      ...(flow ? { flow } : {}),
    };

    return bookingData;
  };

  const handleFormSubmit = async (formData: FormValues) => {
    try {
      setSubmitting(true);
      console.log("Starting form submission process...");

      const bookingData = buildBookingPayload(formData);

      const effectiveBookingType = bookingData.booking_type;
      const effectiveTotalPrice = bookingData.total_price;

      try {
        console.log("Creating booking");

        // New-member-in-this-checkout: the embedded payment just created the
        // membership and stashed its token. The entitlement isn't known yet
        // (provisioned async by the invoice.paid webhook), so pass the token and
        // let the edge function resolve + book the baseline entitlement. Consume
        // (clear) it so a later non-member booking doesn't reuse it. This is the
        // ONE place that touches localStorage — kept out of buildBookingPayload
        // so the builder stays pure and the token is consumed exactly once.
        const pendingMembershipToken = safeLocalStorage.getItem(
          "pendingMembershipToken",
        );
        if (pendingMembershipToken) {
          safeLocalStorage.removeItem("pendingMembershipToken");
        }
        const finalBooking = pendingMembershipToken
          ? { ...bookingData, membership_token: pendingMembershipToken }
          : bookingData;

        console.log(
          "BOOKING SUBMISSION - Complete booking data being saved:",
          finalBooking,
        );

        recordBookingAttempt(bookingId, finalBooking);

        const { data: fnData, error } = await invokeSubmitBookingWithRetry({
          booking: finalBooking,
        });
        const dataWrapper = fnData as Record<string, unknown> | null;
        const data = (
          dataWrapper && "data" in dataWrapper
            ? (dataWrapper["data"] as Record<string, unknown>)
            : dataWrapper
        ) as Record<string, unknown> | null;
        if (error) {
          console.error("Error updating/creating booking:", error);
          // A lost booking must never be silent — this is the only signal we
          // get that a customer hit the failure path (AHD-8846).
          Sentry.captureException(
            error instanceof Error
              ? error
              : new Error(`Booking submit failed: ${error.message}`),
            { tags: { area: "booking-submit" }, extra: { bookingId } },
          );
          toast({
            variant: "destructive",
            title: i18next.t("toast.bookingError.title", { lng }),
            description: i18next.t("toast.bookingError.description", { lng }),
          });
          throw error;
        }

        console.log("Successfully created/updated booking:", data);
        const savedTotal = (data as { total_price?: number } | null)
          ?.total_price;
        console.log(
          "BOOKING SUBMISSION - Saved booking total_price:",
          savedTotal,
        );

        setSubmitted(true);
        onSubmitSuccess({
          ...formData,
          bookingId: data.id,
          clinic_id: selectedClinic,
          booking_type: effectiveBookingType,
          total_price: effectiveTotalPrice,
          packageComponentMap: packageComponentMap,
        });
      } catch (dbError) {
        console.error("Database operation failed:", dbError);
        toast({
          variant: "destructive",
          title: i18next.t("toast.databaseError.title", { lng }),
          description: i18next.t("toast.databaseError.description", { lng }),
        });
        throw dbError;
      }
    } catch (error) {
      console.error("Error in handleFormSubmit:", error);
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  return { handleFormSubmit, buildBookingPayload };
};
